import { useCallback, useEffect, useRef } from 'react';

/**
 * Enhanced user activity detector hook that provides comprehensive
 * input event monitoring with throttling and cleanup capabilities
 */
export const useUserActivityDetector = (
  onActivity: () => void,
  throttleMs = 1000,
  enabled = true
) => {
  const throttleRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(0);

  /**
   * Comprehensive list of user input events to monitor
   */
  const INPUT_EVENTS = [
    // Mouse events
    'mousedown',
    'mousemove',
    'mouseup',
    'click',
    'dblclick',
    'wheel',
    'contextmenu',
    
    // Keyboard events
    'keydown',
    'keyup',
    'keypress',
    
    // Touch events
    'touchstart',
    'touchmove',
    'touchend',
    'touchcancel',
    
    // Scroll events
    'scroll',
    
    // Focus events
    'focus',
    'blur',
    'focusin',
    'focusout',
    
    // Visibility and window events
    'visibilitychange',
    'resize',
    'beforeunload',
    
    // Form events
    'input',
    'change',
    'submit',
    
    // Drag events
    'dragstart',
    'dragend',
    'drop'
  ] as const;

  /**
   * Throttled activity handler to prevent excessive callback calls
   */
  const handleActivity = useCallback((_event: Event) => {
    if (!enabled) return;

    const now = Date.now();
    
    // Ignore if within throttle period
    if (now - lastActivityRef.current < throttleMs) {
      return;
    }

    lastActivityRef.current = now;
    
    // Clear any pending throttled call
    if (throttleRef.current) {
      clearTimeout(throttleRef.current);
    }

    // Throttle the callback
    throttleRef.current = setTimeout(() => {
      onActivity();
      throttleRef.current = null;
    }, 50); // Small delay to batch rapid events
    
  }, [enabled, throttleMs, onActivity]);

  /**
   * Sets up all input event listeners
   */
  const setupListeners = useCallback(() => {
    if (!enabled) return;

    INPUT_EVENTS.forEach(eventType => {
      // Use capture phase for better coverage
      document.addEventListener(eventType, handleActivity, {
        passive: true,
        capture: true
      });
      
      // Also listen on window for some events
      if (['resize', 'beforeunload', 'focus', 'blur'].includes(eventType)) {
        window.addEventListener(eventType, handleActivity, {
          passive: true,
          capture: true
        });
      }
    });
  }, [enabled, handleActivity]);

  /**
   * Removes all input event listeners
   */
  const removeListeners = useCallback(() => {
    INPUT_EVENTS.forEach(eventType => {
      document.removeEventListener(eventType, handleActivity, true);
      window.removeEventListener(eventType, handleActivity, true);
    });
    
    // Clear any pending throttled calls
    if (throttleRef.current) {
      clearTimeout(throttleRef.current);
      throttleRef.current = null;
    }
  }, [handleActivity]);

  /**
   * Setup and cleanup listeners
   */
  useEffect(() => {
    setupListeners();
    return removeListeners;
  }, [setupListeners, removeListeners]);

  return {
    setupListeners,
    removeListeners,
    isEnabled: enabled
  };
};